import { Spinner, StopSquare } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { AiGenerationPhase } from '@/hooks/use-ai-generation-runner';

interface AIGenerateButtonProps {
    onClick: () => void;
    /** Stops the run this button started. Required whenever it can generate. */
    onCancel?: () => void;
    disabled?: boolean;
    /** This button's *own* work is in flight — not merely that something is. */
    isGenerating?: boolean;
    /** Which of the three phases the run is in; `idle` unless it is this one's. */
    phase?: AiGenerationPhase;
    /** Id of the element explaining why the button can't run, when it can't. */
    describedBy?: string;
    /** Fired when a blocked button is clicked, for callers that want to answer. */
    onBlockedClick?: () => void;
    className?: string;
}

export default function AIGenerateButton({
    onClick,
    onCancel,
    disabled = false,
    isGenerating = false,
    phase = 'idle',
    describedBy,
    onBlockedClick,
    className,
}: AIGenerateButtonProps) {
    // `isGenerating` says the work is this button's; `phase` says how far along
    // the stop is. Both are needed: a card whose section is not generating gets
    // the runner's phase anyway, and must not repaint itself as the stop.
    const isRunning = isGenerating && phase !== 'cancelling';
    const isStopping = isGenerating && phase === 'cancelling';
    const isBusy = isRunning || isStopping;

    const label = isStopping ? 'Interrompendo…' : isRunning ? 'Interromper' : 'Gerar com IA';

    return (
        <div className="relative w-full">
            {/* Every decorative layer below is the "generate with AI"
                    flourish, and none of it belongs on a stop control — the
                    halo would keep promising magic under a button that now
                    cancels it. The box they sit in is untouched, so nothing
                    moves when they go. */}
            {!isBusy && (
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -inset-y-3 inset-x-5 rounded-full bg-[radial-gradient(circle,rgba(1,2,107,0.46)_0%,rgba(10,40,157,0.28)_44%,rgba(1,95,252,0.16)_70%,transparent_84%)] opacity-0 blur-2xl transition-all duration-500 ease-out motion-safe:peer-hover:scale-110 motion-safe:peer-hover:opacity-100 motion-safe:peer-focus-visible:scale-110 motion-safe:peer-focus-visible:opacity-100"
                />
            )}

            {/* Never wrapped or unwrapped by state: changing the element
                    tree mid-generation remounts the button and drops focus to
                    <body>, which is the exact thing `aria-disabled` below is
                    here to avoid. */}
            <Button
                onClick={(event) => {
                    // Same slot, second function. The run in flight
                    // is this button's own, so stopping it is the
                    // one thing pressing here can still mean.
                    if (isStopping) {
                        event.preventDefault();
                        return;
                    }

                    if (isRunning) {
                        event.preventDefault();
                        onCancel?.();
                        return;
                    }

                    // `aria-disabled` rather than the native
                    // attribute: a generation polls for up to eight
                    // minutes, and the native one drops focus to
                    // <body> the instant it is applied — stranding a
                    // keyboard user mid-operation. Blocking the
                    // click here keeps the button focused and
                    // announceable, and leaves it able to answer.
                    if (disabled) {
                        event.preventDefault();
                        onBlockedClick?.();
                        return;
                    }
                    onClick();
                }}
                aria-disabled={disabled || isStopping}
                aria-busy={isGenerating}
                aria-describedby={disabled ? describedBy : undefined}
                className={cn(
                    'group/ai peer relative w-full gap-2 overflow-hidden rounded-xl py-6 text-white',
                    disabled && 'cursor-not-allowed opacity-50 hover:bg-primary',
                    // Same box in every phase — width, height and
                    // radius are what must not move. Only the fill
                    // changes: the AI gradient gives way to flat
                    // `primary`, which is what says the button is
                    // no longer the magic but a control.
                    isBusy && 'bg-primary hover:bg-primary/90',
                    isStopping && 'cursor-wait opacity-55',
                    className,
                )}
            >
                {!isBusy && (
                    <span
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-0 rounded-[inherit] bg-[linear-gradient(118deg,rgba(1,2,107,0.18)_0%,rgba(6,28,128,0.08)_22%,rgba(14,76,232,0.36)_48%,rgba(2,22,120,0.34)_68%,rgba(255,255,255,0.12)_84%,rgba(255,255,255,0)_100%)] opacity-0 transition-opacity duration-500 ease-out motion-safe:group-hover/ai:opacity-100 motion-safe:group-focus-visible/ai:opacity-100"
                    />
                )}
                {!isBusy && (
                    <span
                        aria-hidden="true"
                        className="pointer-events-none absolute -left-10 top-1/2 size-20 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(107,164,255,0.5)_0%,rgba(107,164,255,0)_72%)] opacity-0 blur-xl transition-all duration-500 ease-out motion-safe:group-hover/ai:left-1/4 motion-safe:group-hover/ai:opacity-100 motion-safe:group-focus-visible/ai:left-1/4 motion-safe:group-focus-visible/ai:opacity-100"
                    />
                )}
                {!isBusy && (
                    <span
                        aria-hidden="true"
                        className="pointer-events-none absolute -right-10 top-1/2 size-24 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(1,2,107,0.58)_0%,rgba(24,87,255,0)_72%)] opacity-0 blur-2xl transition-all duration-500 ease-out motion-safe:group-hover/ai:right-[18%] motion-safe:group-hover/ai:opacity-100 motion-safe:group-focus-visible/ai:right-[18%] motion-safe:group-focus-visible/ai:opacity-100"
                    />
                )}
                {!isBusy && (
                    <span
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-x-4 top-0 h-px bg-card/50 opacity-0 transition-opacity duration-500 ease-out motion-safe:group-hover/ai:opacity-100 motion-safe:group-focus-visible/ai:opacity-100"
                    />
                )}
                <span className="relative z-10 inline-flex items-center gap-2">
                    {isStopping && (
                        <Spinner aria-hidden="true" className="size-4 motion-safe:animate-spin" />
                    )}
                    {isRunning && <StopSquare aria-hidden="true" className="size-4" />}
                    {label}
                </span>
            </Button>
        </div>
    );
}
